import { inject, Pipe, PipeTransform } from '@angular/core'
import { LanguageService } from '../../core/language.service'
import { HideWalletService } from '../../core/hide-wallet-service'

// Define the interface for our pipe's options for type safety
export interface FormatNumberOptions {
	isCurrency?: boolean
	showCurrency?: boolean
	doCommaSeparate?: boolean
	doTranslate?: boolean
	doHideNumbers?: boolean
}

@Pipe({
	name: 'NumberFormatter',
	standalone: true,
})
export class NumberFormatterPipe implements PipeTransform {
	private languageService = inject(LanguageService)
	readonly hideWalletService = inject(HideWalletService)

	/**
	 * A helper function to add comma separators to a number string.
	 */
	private addCommas(numStr: string): string {
		return numStr.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
	}

	/**
	 * A helper function to translate an English number string (with commas)
	 * into Persian digits and separators.
	 */
	private toPersian(enStr: string): string {
		return enStr
			.replace(/,/g, '٬') // Translate comma
			.replace(/[0-9]/g, d => '۰۱۲۳۴۵۶۷۸۹'[parseInt(d, 10)]) // Translate digits
	}

	transform(value: number | null | undefined, options: FormatNumberOptions = {}): string | undefined {
		if (value == null || isNaN(value)) {
			return undefined
		}

		// Set default values for the options
		const config = {
			isCurrency: options.isCurrency ?? true,
			showCurrency: options.showCurrency ?? false,
			doCommaSeparate: options.doCommaSeparate ?? true,
			doTranslate: options.doTranslate ?? true,
			doHideNumbers: options.doHideNumbers ?? true,
		}
		const currentLang = this.languageService.currentLanguage()
		let formattedString = value.toString()

		const currentHideStatus = this.hideWalletService.currentHideStatus()

		const hideNumbers = currentHideStatus === 'hide'

		if (hideNumbers && config.doHideNumbers) {
			// Replace every digit in the number with a '●'
			const hiddenString = value.toString().replace(/\d/g, '*')

			return hiddenString
		}

		// 1. Conditionally add comma separators
		if (config.doCommaSeparate) {
			formattedString = this.addCommas(formattedString)
		}

		// 2. Conditionally translate the digits to Persian
		if (config.doTranslate && currentLang === 'fa') {
			formattedString = this.toPersian(formattedString)
		}

		// 3. Conditionally add the currency symbol
		if (config.isCurrency) {
			formattedString = currentLang === 'fa' ? `${formattedString}` : `${formattedString}`
		}

		if (config.showCurrency) {
			formattedString = currentLang === 'fa' ? `${formattedString} ﷼` : `${formattedString} IRR`
		}

		return formattedString
	}
}
